import { useCallback, useMemo, useRef } from "react";
import { reportDocumentTableFailure } from "./documentTableFailure";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTablePreviewState } from "./useTablePreviewState";
import type { useTableSigningState } from "./useTableSigningState";
type Options = Pick<ReturnType<typeof documentTableOptions>, "documents" | "store"> &
  Pick<ReturnType<typeof useTablePreviewState>, "toast"> &
  Pick<ReturnType<typeof useTableBasics>, "t"> &
  Pick<
    ReturnType<typeof useTableSigningState>,
    "setSelectedDocumentsForSigning" | "setIsSignDialogOpen"
  >;
export function useTableSigning(options: Options) {
  const { documents, store, toast, t, setSelectedDocumentsForSigning, setIsSignDialogOpen } =
    options;
  // Use provided documents or empty array (empty state will show). Memoised so
  // the fallback doesn't mint a new array every render — `openSignDialog`
  // depends on it.
  const allDocuments = useMemo(() => documents ?? [], [documents]);

  // Resolve the current multi-selection to signable documents the SAME way every
  // other bulk action does — through the store resolver, with the rendered rows
  // as extraRows. Filtering only the rendered rows (what this used to do) breaks
  // on surfaces whose rows live outside the MST map (project views use the
  // `projectDocuments` cache — the "dialog showed 0 documents" bug) and silently
  // shrinks the set for a selection made across pages, which the resolver's
  // server recovery covers. Resolved on open, not in render, since it may need a
  // round-trip.
  //
  // Deliberately NO folderIds: the resolver drops unresolved ids that sit inside
  // a checked folder's subtree, on the premise that the action handles the
  // folder server-side. True for the trash RPC and the zip builder; NOT true of
  // signing — `startSigning` only ever gets `documentIds` and nothing signs a
  // folder. Passing them resolved a folder-cascade selection (descendants no
  // surface renders) to zero documents, with `unresolvedCount` 0 so not even a
  // warning. Without them every cascaded id is recovered by the server lookup.
  // The resolve is a round-trip, so the selection can move under it: unchecking
  // a row (or a second click landing out of order) while it is in flight would
  // otherwise open the dialog on the pre-change set and submit THAT to
  // startSigning. So: one resolve at a time, and the result is only used if the
  // selection it was computed from is still the current one.
  const isResolvingSignature = useRef(false);
  const openSignDialog = useCallback(async () => {
    if (!store || isResolvingSignature.current) return;
    const selectionAtRequest = store.selectedDocumentIds.join(",");
    isResolvingSignature.current = true;
    try {
      const { docs, unresolvedCount } = await store.resolveSelectionForBulkAction(allDocuments);
      if (store.selectedDocumentIds.join(",") !== selectionAtRequest) {
        toast({
          title: t.common.warning,
          description: "The selection changed while it was being checked — press Sign again.",
        });
        return;
      }
      if (unresolvedCount > 0) {
        toast({
          title: t.common.warning,
          description: `${unresolvedCount} selected document(s) could not be found`,
          variant: "destructive",
        });
      }
      // Nothing resolved: the dialog would open empty with its submit button
      // permanently disabled and no clue why. The warning above is the reason;
      // if there wasn't one, say so here.
      if (docs.length === 0) {
        if (unresolvedCount === 0) {
          toast({
            title: t.common.warning,
            description: "No documents available to sign",
            variant: "destructive",
          });
        }
        return;
      }
      setSelectedDocumentsForSigning(
        docs.map((d) => ({ id: Number(d.id), filename: d.filename, size: d.size ?? 0 }))
      );
      setIsSignDialogOpen(true);
    } catch (error) {
      // Never open the dialog on a selection we could not verify — signing the
      // wrong set of documents is not recoverable.
      reportDocumentTableFailure("signing_selection_resolve", error);
      toast({ title: t.common.error, variant: "destructive" });
    } finally {
      isResolvingSignature.current = false;
    }
  }, [store, allDocuments, t, toast, setIsSignDialogOpen, setSelectedDocumentsForSigning]);
  return { allDocuments, openSignDialog };
}
